import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@deepguide-ai/ui/dialog'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { ExternalLink, Loader2, Plus } from 'lucide-react'
import type { RepositoryItem } from './ConnectionScanCard'

export function AddRepositoryDialog({
  open,
  onOpenChange,
  providerName,
  repos,
  isRepoLoading,
  isConnecting,
  isRefreshing,
  onConnect,
  onOpenProvider,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  providerName: string
  repos: RepositoryItem[]
  isRepoLoading: boolean
  isConnecting: boolean
  isRefreshing: boolean
  onConnect: (repo: RepositoryItem) => void
  onOpenProvider?: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='border-border max-w-[560px] gap-0 overflow-hidden border bg-card p-0 shadow-[rgba(0,0,0,0.1)_0px_14px_28px_-6px,rgba(0,0,0,0.06)_0px_2px_4px_-1px,rgba(84,72,49,0.08)_0px_0px_0px_1px]'>
        <DialogHeader className='bg-card px-6 py-4 pr-12'>
          <DialogTitle className='font-heading text-text-secondary text-[15px] font-semibold'>
            Add {providerName} repository
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-5 bg-card px-6 py-5'>
          <div className='flex items-start justify-between gap-6 pb-4'>
            <p className='font-body max-w-[360px] text-[12px] leading-[1.6] text-text-body'>
              Need another repository? Update {providerName} and connect it
              here.
            </p>
            <button
              type='button'
              onClick={onOpenProvider}
              disabled={!onOpenProvider || isRefreshing}
              aria-busy={isRefreshing}
              className='font-body bg-primary text-primary-foreground hover:bg-primary/90 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-sm px-3 text-[12px] font-semibold transition-colors disabled:opacity-60'
            >
              {isRefreshing ? (
                <Loader2 className='h-3 w-3 animate-spin' />
              ) : (
                <ExternalLink className='h-3 w-3' />
              )}
              {isRefreshing ? 'Syncing...' : `Manage in ${providerName}`}
            </button>
          </div>

          <div className='space-y-3'>
            <div className='text-[10px] font-medium tracking-[1.2px] text-text-subtle uppercase'>
              Available to connect
            </div>
            {isRepoLoading ? (
              <div className='text-text-placeholder flex items-center justify-center py-6 text-[12px]'>
                <Loader2 className='h-4 w-4 animate-spin' />
              </div>
            ) : repos.length === 0 ? (
              <div className='text-text-subtle py-1 text-[12px]'>
                No authorized repositories are available to connect yet.
              </div>
            ) : (
              <div className='border-border overflow-hidden rounded-sm border bg-card'>
                <ScrollArea
                  viewClassName='max-h-[300px] [&>div]:!block'
                  type='hover'
                >
                  {repos.map((repo, index) => (
                    <div
                      key={repo.id}
                      className={`flex items-center justify-between gap-4 px-4 py-3 ${
                        index === 0 ? '' : 'border-t border-separator-subtle'
                      }`}
                    >
                      <div className='min-w-0 flex-1'>
                        <div className='font-body text-text-secondary truncate text-[13px] font-medium'>
                          {repo.fullName}
                        </div>
                      </div>
                      <button
                        type='button'
                        onClick={() => onConnect(repo)}
                        disabled={isConnecting}
                        className='font-body border-status-blue/30 bg-status-blue-bg inline-flex h-7 shrink-0 items-center gap-1.5 rounded-sm border px-2.5 text-[11px] font-medium text-status-blue-strong transition-colors hover:border-status-blue/50 hover:bg-cobalt-tonal-hover disabled:opacity-60'
                      >
                        {isConnecting ? (
                          <Loader2 className='h-3 w-3 animate-spin' />
                        ) : (
                          <Plus className='h-3 w-3' />
                        )}
                        Connect
                      </button>
                    </div>
                  ))}
                </ScrollArea>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
